import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Download, CheckCircle, FileText, Mail, RefreshCcw, Link2, Pencil } from 'lucide-react';
import { formatDate } from '../../utils/dateFormat';
import { logger } from '../../utils/logger';
import { IssueRefundModal } from '../../components/admin/IssueRefundModal';
import { LinkReceiptToQuoteModal } from '../../components/admin/LinkReceiptToQuoteModal';
import { ReceiptPricingRows, ReceiptPaymentSummary } from '../../components/admin/ReceiptPricingBreakdown';
import { CombinedReceiptDetails } from '../../components/admin/CombinedReceiptDetails';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ViewReceipt = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  // For quote-linked receipts: the linked quote (used to detect orphan/ledger-drift).
  const [linkedQuote, setLinkedQuote] = useState(null);
  const [repairing, setRepairing] = useState(false);
  const password = sessionStorage.getItem('admin_password');

  useEffect(() => {
    if (!password) {
      navigate('/admin/login');
      return;
    }
    fetchReceipt();
  }, [id]);

  // Whenever we have a quote-linked receipt, fetch the quote so we can tell if
  // its payment ledger is in sync (payment referencing THIS receipt exists) or
  // if this receipt is orphaned (linked but the ledger no longer has the entry).
  useEffect(() => {
    if (!receipt?.quote_id) {
      setLinkedQuote(null);
      return;
    }
    fetch(`${API_URL}/api/admin/quotes/${receipt.quote_id}`, {
      headers: { 'Authorization': `Basic ${btoa(':' + password)}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(q => setLinkedQuote(q))
      .catch(err => logger.error('Failed to fetch linked quote:', err));
  }, [receipt?.quote_id, password]);

  const isOrphanedQuoteLink = (() => {
    if (!receipt?.quote_id || !linkedQuote) return false;
    const payments = linkedQuote.payments || [];
    const matched = payments.find(p =>
      p.receipt_id === receipt.id
      || (p.receipt_number && p.receipt_number === receipt.receipt_number)
    );
    return !matched;
  })();

  const handleRepairLedger = async () => {
    if (!receipt?.quote_id || !linkedQuote) return;
    if (!window.confirm(
      `Add a payment entry to ${linkedQuote.quote_number} for this receipt (${receipt.currency || 'CI$'}${(receipt.total || 0).toFixed(2)})? The quote's balance will be updated.`
    )) return;
    setRepairing(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/receipts/${receipt.id}/link-to-quote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${btoa(':' + password)}`,
        },
        body: JSON.stringify({ quote_id: receipt.quote_id }),
      });
      if (response.ok) {
        await fetchReceipt();
        // Re-fetch the linked quote so isOrphanedQuoteLink flips off.
        const q = await response.json();
        setLinkedQuote(q);
        alert(`Ledger repaired — ${receipt.receipt_number} is now on ${linkedQuote.quote_number}'s payment ledger.`);
      } else {
        // Try JSON first, fall back to raw text so we surface *something* useful
        // even when the backend returns a non-JSON error body.
        let reason = '';
        try {
          const err = await response.json();
          reason = err.detail || err.message || JSON.stringify(err);
        } catch {
          try { reason = (await response.text()) || ''; } catch { reason = ''; }
        }
        if (!reason) reason = `HTTP ${response.status}`;
        logger.error('Repair failed', { status: response.status, reason });
        alert(`Failed to repair ledger: ${reason}`);
      }
    } catch (err) {
      logger.error('Repair failed:', err);
      alert('Network error. Please try again.');
    } finally {
      setRepairing(false);
    }
  };

  const fetchReceipt = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/receipts/${id}`, {
        headers: {
          'Authorization': `Basic ${btoa(':' + password)}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setReceipt(data);
      } else if (response.status === 401) {
        navigate('/admin/login');
      } else {
        navigate('/admin/receipts');
      }
    } catch (err) {
      logger.error('Failed to fetch receipt:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/receipts/${id}/download-pdf`, {
        headers: {
          'Authorization': `Basic ${btoa(':' + password)}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Receipt_${(receipt.client_name || 'Receipt').replace(/\s/g, '_')}_${(receipt.receipt_number || receipt.id || '').replace(/\//g, '-')}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Failed to download PDF');
      }
    } catch (err) {
      logger.error('Download error:', err);
      alert('Failed to download PDF. Please try again.');
    }
  };

  const getPaymentMethodLabel = (method) => {
    const methodMap = {
      'bank_transfer': 'Bank Transfer',
      'wire': 'Wire Transfer',
      'cash': 'Cash',
      'card': 'Credit/Debit Card'
    };
    return methodMap[method] || method;
  };

  const handleSendEmail = async () => {
    if (!window.confirm(`Send receipt ${receipt.receipt_number} to ${receipt.client_email}?`)) return;
    setSending(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/receipts/${id}/send-email`, {
        method: 'POST',
        headers: { 'Authorization': `Basic ${btoa(':' + password)}` }
      });
      const data = await response.json();
      if (response.ok) {
        alert(data.message || `Receipt sent to ${receipt.client_email}`);
      } else {
        alert(`Failed to send: ${data.detail || 'Unknown error'}`);
      }
    } catch (err) {
      logger.error('Send email error:', err);
      alert('Failed to send email. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-foreground/60">Loading receipt...</p>
        </div>
      </div>
    );
  }

  if (!receipt) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-border">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4 admin-header-row">
            <div>
              <h1 className="text-2xl font-bold text-green-600">Receipt {receipt.receipt_number}</h1>
              <p className="text-sm text-foreground/60">Payment confirmation</p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => navigate('/admin/receipts')}
                data-testid="receipt-back"
                className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-gray-50 transition-colors"
              >
                ← Back
              </button>
              <button
                onClick={handleSendEmail}
                disabled={sending}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                data-testid="email-receipt-btn"
                title={`Re-send receipt PDF to ${receipt.client_email}`}
              >
                <Mail className="w-4 h-4" />
                {sending ? 'Sending…' : 'Email Receipt'}
              </button>
              <button
                onClick={handleDownloadPDF}
                data-testid="receipt-download"
                className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download PDF
              </button>
              <button
                onClick={() => navigate(`/admin/receipts/${receipt.id}/edit`)}
                data-testid="edit-receipt-btn"
                disabled={receipt.receipt_kind === 'combined'}
                className="px-4 py-2 text-sm border-2 border-primary text-primary rounded-lg hover:bg-primary/5 transition-colors flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                title={receipt.receipt_kind === 'combined' ? 'Issued combined allocations are read-only' : "Edit this receipt's line items, amounts, or remaining balance"}
              >
                <Pencil className="w-4 h-4" />
                Edit
              </button>
              {!receipt.invoice_id && !receipt.quote_id && (
                <button
                  onClick={() => setLinkOpen(true)}
                  data-testid="link-receipt-to-quote-btn"
                  className="px-4 py-2 text-sm border-2 border-primary text-primary rounded-lg hover:bg-primary/5 transition-colors flex items-center gap-2"
                  title="Attach this standalone receipt to an existing quote's payment ledger"
                >
                  <Link2 className="w-4 h-4" />
                  Link to Quote
                </button>
              )}
              {isOrphanedQuoteLink && (
                <button
                  onClick={handleRepairLedger}
                  disabled={repairing}
                  data-testid="repair-quote-ledger-btn"
                  className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                  title={`This receipt shows Quote Reference ${linkedQuote?.quote_number} but no matching payment exists on the quote's ledger. Click to add it.`}
                >
                  <Link2 className="w-4 h-4" />
                  {repairing ? 'Repairing…' : `Repair ${linkedQuote?.quote_number || 'Quote'} Ledger`}
                </button>
              )}
              <button
                onClick={() => setRefundOpen(true)}
                data-testid="open-refund-from-receipt-btn"
                disabled={receipt.receipt_kind === 'combined'}
                className="px-4 py-2 text-sm border-2 border-red-500 text-red-600 rounded-lg hover:bg-red-50 transition-colors flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                title={receipt.receipt_kind === 'combined' ? 'Cash and credit must be reconciled separately before a refund' : 'Issue a refund against this receipt'}
              >
                <RefreshCcw className="w-4 h-4" />
                Issue Refund
              </button>
            </div>
          </div>
        </div>
      </div>

      <IssueRefundModal
        open={refundOpen}
        onClose={() => setRefundOpen(false)}
        sourceType="receipt"
        sourceId={receipt.id}
        sourceNumber={receipt.receipt_number}
        paidAmount={(() => {
          if (receipt.pricing_view) return receipt.pricing_view.amount_paid;
          const total = parseFloat(receipt.total) || 0;
          const owed = parseFloat(receipt.quote_balance_remaining) || 0;
          return owed > 0 ? Math.max(total - owed, 0) : total;
        })()}
        currency={receipt.currency || 'CI$'}
        clientName={receipt.client_name}
        onIssued={() => alert(`Refund issued and emailed to ${receipt.client_email}.`)}
      />

      <LinkReceiptToQuoteModal
        open={linkOpen}
        onClose={() => setLinkOpen(false)}
        receipt={receipt}
        onLinked={async (quote) => {
          // Refresh receipt data BEFORE surfacing the alert so the header re-renders
          // (hides the Link button, shows the new Quote Reference).
          await fetchReceipt();
          const paid = (quote.payments || []).reduce((s, p) => s + (Number(p.amount) || 0), 0);
          const balance = Math.max((Number(quote.total) || 0) - paid, 0);
          alert(`Receipt ${receipt.receipt_number} linked to Quote ${quote.quote_number}. New balance: ${receipt.currency || 'CI$'}${balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}.`);
        }}
      />

      {/* Content */}
      <div className="container mx-auto px-6 py-8">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Main Content */}
          <div className="min-w-0 space-y-6 lg:col-span-2">
            {/* Orphan-ledger warning banner (quote-linked receipt without a matching payment on the quote) */}
            <CombinedReceiptDetails receipt={receipt} />
            {isOrphanedQuoteLink && (
              <div
                className="bg-red-50 border-2 border-red-200 rounded-xl p-4 flex items-start gap-3"
                data-testid="orphan-ledger-warning"
              >
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
                  <Link2 className="w-5 h-5 text-red-600" />
                </div>
                <div className="flex-1 text-sm">
                  <p className="font-semibold text-red-900 mb-0.5">Quote ledger out of sync</p>
                  <p className="text-red-800/90">
                    This receipt references <span className="font-mono font-semibold">{linkedQuote?.quote_number}</span> but the quote&apos;s payment ledger has no matching entry.
                    Click <span className="font-semibold">&quot;Repair {linkedQuote?.quote_number} Ledger&quot;</span> in the header to add the payment and update the balance.
                  </p>
                </div>
              </div>
            )}

            {/* Confirmation Banner */}
            <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6 text-center">
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-3" />
              <h2 className="text-2xl font-bold text-green-900 mb-2">Payment Received</h2>
              <p className="text-green-700">Thank you! Your payment has been received and processed.</p>
            </div>

            {/* Receipt Details */}
            <div className="bg-white rounded-xl border border-border shadow-sm p-6">
              <h2 className="text-lg font-bold text-foreground mb-4">Receipt Information</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-foreground/60">Receipt Number</p>
                  <p className="font-mono font-semibold text-green-600">{receipt.receipt_number}</p>
                </div>
                <div>
                  <p className="text-sm text-foreground/60">Receipt Date</p>
                  <p className="font-medium text-foreground">{formatDate(receipt.receipt_date)}</p>
                </div>
                <div>
                  {receipt.invoice_id && receipt.invoice_number ? (
                    <>
                      <p className="text-sm text-foreground/60">Invoice Reference</p>
                      <button
                        onClick={() => navigate(`/admin/invoices/${receipt.invoice_id}`)}
                        className="font-mono font-semibold text-primary hover:underline"
                        data-testid="receipt-invoice-reference"
                      >
                        {receipt.invoice_number}
                      </button>
                    </>
                  ) : receipt.quote_id && receipt.quote_number ? (
                    <>
                      <p className="text-sm text-foreground/60">Quote Reference</p>
                      <button
                        onClick={() => navigate(`/admin/quotes/${receipt.quote_id}`)}
                        className="font-mono font-semibold text-primary hover:underline"
                        data-testid="receipt-quote-reference"
                      >
                        {receipt.quote_number}
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-foreground/60">Reference</p>
                      <p className="font-medium text-foreground/60 italic">Standalone receipt</p>
                    </>
                  )}
                </div>
                <div>
                  <p className="text-sm text-foreground/60">Payment Date</p>
                  <p className="font-medium text-foreground">{formatDate(receipt.payment_date)}</p>
                </div>
                <div>
                  <p className="text-sm text-foreground/60">Payment Method</p>
                  <p className="font-medium text-foreground">{getPaymentMethodLabel(receipt.payment_method)}</p>
                </div>
              </div>
            </div>

            {/* Payment Breakdown */}
            <div className="bg-white rounded-xl border border-border shadow-sm p-6">
              <h2 className="text-lg font-bold text-foreground mb-4">Payment Breakdown</h2>
              <div className="min-w-0">
                <table className="w-full table-fixed">
                  <thead className="bg-gray-50 border-b border-border">
                    <tr>
                      <th className="w-[7%] px-1 py-3 text-left text-xs text-foreground/60">#</th>
                      <th className="w-[37%] px-1 py-3 text-left text-xs text-foreground/60">Service</th>
                      <th className="w-[10%] px-1 py-3 text-center text-xs text-foreground/60">Qty</th>
                      <th className="w-[23%] px-1 py-3 text-right text-xs text-foreground/60">Price</th>
                      <th className="w-[23%] px-1 py-3 text-right text-xs text-foreground/60">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {(receipt.items || []).map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-1 py-3 text-xs text-foreground/70">{index + 1}</td>
                        <td className="break-words px-1 py-3 text-xs sm:text-sm" data-testid={`receipt-item-${index}`}>{item.description}{receipt.pricing_view?.line_discounts?.[index] && <p className="mt-1 text-xs text-emerald-700" data-testid={`receipt-item-discount-${index}`}>{receipt.pricing_view.line_discounts[index]}</p>}</td>
                        <td className="px-1 py-3 text-center text-xs">{item.quantity}</td>
                        <td className="break-words px-1 py-3 text-right text-xs" data-testid={`receipt-item-price-${index}`}>{receipt.currency || 'CI$'}{(Number(item.unit_price) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td className="break-words px-1 py-3 text-right text-xs font-semibold" data-testid={`receipt-item-total-${index}`}>{receipt.currency || 'CI$'}{(Number(item.total) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className={`${receipt.quote_balance_remaining > 0 ? 'bg-gray-50 border-t-2 border-gray-400' : 'bg-green-50 border-t-2 border-green-500'}`}>
                    <ReceiptPricingRows receipt={receipt} />
                    <tr>
                      <td colSpan="4" className={`px-4 py-4 text-right font-bold ${receipt.quote_balance_remaining > 0 ? 'text-gray-700' : 'text-green-900'}`}>
                        {receipt.pricing_view?.total_label || (receipt.quote_balance_remaining > 0 ? 'TOTAL INVOICE:' : 'TOTAL PAID:')}
                      </td>
                      <td data-testid="receipt-total" className={`break-words px-1 py-4 text-right text-sm font-bold sm:text-lg ${receipt.quote_balance_remaining > 0 ? 'text-gray-800' : 'text-green-600'}`}>
                        {receipt.currency || 'CI$'}{(Number(receipt.total) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <ReceiptPaymentSummary receipt={receipt} />
              {!receipt.pricing_view && receipt.quote_balance_remaining > 0 && (() => {
                const invoiceTotal = receipt.total || 0;
                const owed = receipt.quote_balance_remaining || 0;
                const paid = Math.max(invoiceTotal - owed, 0);
                const pct = invoiceTotal > 0 ? (paid / invoiceTotal) * 100 : 0;
                return (
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="receipt-breakdown">
                    <div className="bg-gray-100 rounded-lg p-4 text-center">
                      <p className="text-[11px] uppercase tracking-wide text-gray-700 font-semibold">Total Invoice</p>
                      <p className="text-xl font-bold text-gray-900">{receipt.currency || 'CI$'}{invoiceTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 text-center">
                      <p className="text-[11px] uppercase tracking-wide text-green-700 font-semibold">Amount Paid</p>
                      <p className="text-xl font-bold text-green-700">{receipt.currency || 'CI$'}{paid.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                      <p className="text-[10px] text-foreground/50">{pct.toFixed(1)}% paid</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-4 text-center">
                      <p className="text-[11px] uppercase tracking-wide text-amber-800 font-semibold">Remaining</p>
                      <p className="text-xl font-bold text-amber-800">{receipt.currency || 'CI$'}{owed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Notes */}
            {receipt.notes && (
              <div className="bg-white rounded-xl border border-border shadow-sm p-6">
                <h2 className="text-lg font-bold text-foreground mb-4">Notes</h2>
                <p className="text-foreground/70 whitespace-pre-wrap">{receipt.notes}</p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Actions */}
            <div className="bg-white rounded-xl p-6 border border-border shadow-sm sticky top-6 space-y-4">
              <h3 className="text-lg font-bold text-foreground mb-4">Actions</h3>
              
              <button
                onClick={handleDownloadPDF}
                data-testid="receipt-sidebar-download"
                className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
              >
                <Download className="w-5 h-5" />
                Download PDF
              </button>

              {receipt.invoice_id && <button
                onClick={() => navigate(`/admin/invoices/${receipt.invoice_id}`)}
                data-testid="receipt-sidebar-invoice"
                className="w-full px-4 py-3 border-2 border-primary text-primary rounded-lg hover:bg-primary/5 transition-colors flex items-center justify-center gap-2"
              >
                <FileText className="w-5 h-5" />
                View Invoice
              </button>}
            </div>

            {/* Client Info */}
            <div className="bg-white rounded-xl p-6 border border-border shadow-sm">
              <h3 className="text-lg font-bold text-foreground mb-4">Client Information</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-foreground/60">Name</p>
                  <p className="break-words font-medium text-foreground" data-testid="receipt-client-name">{receipt.client_name}</p>
                </div>
                <div>
                  <p className="text-sm text-foreground/60">Email</p>
                  <p className="break-all font-medium text-foreground" data-testid="receipt-client-email">{receipt.client_email}</p>
                </div>
                {receipt.client_phone && (
                  <div>
                    <p className="text-sm text-foreground/60">Phone</p>
                    <p className="break-words font-medium text-foreground" data-testid="receipt-client-phone">{receipt.client_phone}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <p className="text-sm text-green-900 font-medium mb-1">✓ Official Receipt</p>
              <p className="text-xs text-green-700">This is an official payment receipt. Please retain for your records.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
